<template>
  <div
    class="sb-timeline"
    data-sb-regression="sb-timeline"
    :style="{ minWidth: `${minWidth}px` }"
  >
    <SbProgressBar
      :value="width"
      :variation="variation"
    />
    <div class="sb-timeline__breakpoints">
      <!--
      @slot for timeline content. Loops through breakpoints
      -->
      <slot
        v-for="point in breakpoints"
        :breakpoint="point"
        :active="isActive(point)"
      />
    </div>
  </div>
</template>

<script>
import isEqual from 'lodash/isEqual';
import throttle from 'lodash/throttle';
import sum from 'lodash/sum';
import ResizeObserver from 'resize-observer-polyfill';

import SbProgressBar from '@src/components/ProgressBar/SbProgressBar.vue';

const PAD = 50;

export default {
  name: 'CurrentTimeline',
  components: {
    SbProgressBar,
  },
  provide() {
    return {
      sbTimeline: this.sbTimeline,
    };
  },
  props: {
    /**
     * A list of points to be iterated over for the scoped slot `SbProgressBarBreakPoint`
     */
    breakpoints: {
      type: Array,
      required: true,
      default: () => ([]),
    },

    /**
     * The current active step for the Timeline. Should be one of the breakpoints.
     */
    currentStep: {
      default: null,
    },

    /**
     * Function run to compare equality of the currentStep to one of the breakpoints. `(breakpoint, currentStep) => {}`
     */
    compareFunc: {
      type: Function,
      default: isEqual,
    },
    /**
     * Style variation to give additional meaning.
     * `primary, error, warning, success, disabled`
     */
    variation: {
      type: String,
      default: null,
      validator: (value) => {
        return value === null;
      },
    },
  },
  data: () => ({
    sbTimeline: {
      numberOfBreakPoints: 0,
      els: [],
    },
    width: 0,
    minWidth: null,
    observer: null,
  }),
  watch: {
    breakpoints: {
      handler(newVal) {
        this.sbTimeline.numberOfBreakPoints = newVal.length;
        this.$nextTick(this.setWidths);
      },
      immediate: true,
    },
    currentStep: {
      handler() {
        this.setWidths();
      },
    },
  },
  mounted() {
    this.$nextTick(() => {
      const handleResize = throttle(this.setWidths, 500);
      this.observer = new ResizeObserver(handleResize);
      if (this.$el instanceof Element) this.observer.observe(this.$el);
    });
  },
  unmounted() {
    if (this.observer) this.observer.disconnect();
  },
  methods: {
    isActive(breakpoint) {
      return this.compareFunc(breakpoint, this.currentStep);
    },
    setWidths() {
      this.setWidth();
      this.setMinWidth(PAD);
    },
    setWidth() {
      this.width = this.calculatePercentage();
    },
    setMinWidth(pad) {
      const widths = this.extractWidths();
      const minWidth = sum(widths);
      this.minWidth = minWidth + (widths.length - 1) * pad;
    },
    calculatePercentage() {
      const n = this.sbTimeline.numberOfBreakPoints;
      const i = this.breakpoints.findIndex(this.isActive);
      const widths = this.extractWidths();
      if (n === 0 || widths.length === 0) return 0;
      else if (i === n - 1) return 1;
      const l = this.$el.offsetWidth;
      const k = sum(widths);
      const s = (l - k) / (n - 1);
      let j = 0;
      let ii = i;
      while (ii >= 0) {
        j += i === ii ? widths[ii] / 2 : widths[ii];
        // eslint-disable-next-line no-plusplus
        ii--;
      }
      j += i * s;
      return j / l;
    },
    extractWidths() {
      return this.sbTimeline.els.map((el) => el.offsetWidth);
    },
  },
};
</script>

<style scoped lang='scss'>
@import '@src/components/Timeline/style/sb-timeline.scss';
</style>
